// apps/buildlog/src/pages/Analytics.tsx
import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, getCurrentUser } from '../services/firebase';
import { AppLayout } from '../components/layout/AppLayout';
import { AnalyticsData } from '../types/types';
import { BarChart3, FileText, CheckCircle, Clock } from 'lucide-react';
import { StatCard } from '../components';

export function Analytics() {
  const [stats, setStats] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Buildlog — Analytics';
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    const user = getCurrentUser();
    if (!user) return;

    try {
      // Fetch all entries for this user across all projects
      const q = query(
        collection(db, 'buildLogs'),
        where('userId', '==', user.uid)
      );
      const snap = await getDocs(q);
      const entries = snap.docs.map(d => d.data());

      // Entry frequency — last 30 days bucketed by date
      const now = Date.now();
      const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
      const freqMap: Record<string, number> = {};
      const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
      let thisWeek = 0;

      entries.forEach(e => {
        const ms = e.timestamp?.toMillis?.() ?? 0;
        if (ms > weekAgo) thisWeek++;
        if (ms > thirtyDaysAgo) {
          const dateKey = new Date(ms).toISOString().slice(0, 10);
          freqMap[dateKey] = (freqMap[dateKey] ?? 0) + 1;
        }
      });

      const entryFrequency = Object.entries(freqMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, count]) => ({ date, count }));

      // Tag frequency
      const tagMap: Record<string, number> = {};
      entries.forEach(e => {
        (e.tags ?? []).forEach((t: string) => {
          tagMap[t] = (tagMap[t] ?? 0) + 1;
        });
      });
      const topTags = Object.entries(tagMap)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8)
        .map(([tag, count]) => ({ tag, count }));

      // Audience distribution
      const audienceMap: Record<string, number> = {
        developer: 0, merchant: 0, investor: 0, consumer: 0, founder: 0,
      };
      entries.forEach(e => {
        if (e.primaryAudience && audienceMap[e.primaryAudience] !== undefined) {
          audienceMap[e.primaryAudience]++;
        }
      });

      const published = entries.filter(e => e.published === true).length;

      setStats({
        totalEntries: entries.length,
        entriesThisWeek: thisWeek,
        publishedEntries: published,
        unpublishedEntries: entries.length - published,
        entriesByAudience: audienceMap as AnalyticsData['entriesByAudience'],
        topTags,
        entryFrequency,
      });
    } catch (err) {
      console.error('Analytics load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center py-40">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-brand-blue border-t-transparent" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1">
            Analytics
          </h1>
          <p className="text-slate-500">Patterns in your build history.</p>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Entries" value={stats?.totalEntries ?? 0} icon={<FileText className="text-brand-blue" size={20} />} />
          <StatCard label="This Week" value={stats?.entriesThisWeek ?? 0} icon={<Clock className="text-green-500" size={20} />} />
          <StatCard label="Published" value={stats?.publishedEntries ?? 0} icon={<CheckCircle className="text-brand-orange" size={20} />} />
          <StatCard label="Unpublished" value={stats?.unpublishedEntries ?? 0} icon={<BarChart3 className="text-slate-400" size={20} />} />
        </div>

        {/* Top tags */}
        {stats?.topTags && stats.topTags.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4" >
              Top Tags
            </h2>
            <div className="space-y-2">
              {stats.topTags.map(({ tag, count }) => {
                const pct = Math.round((count / (stats.totalEntries || 1)) * 100);
                return (
                  <div key={tag} className="flex items-center gap-3">
                    <span className="text-sm font-mono text-slate-600 w-28 truncate">#{tag}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-blue rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-400 font-mono w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Entry frequency — last 30 days */}
        {stats?.entryFrequency && stats.entryFrequency.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4" >
              Entry Frequency — Last 30 Days
            </h2>
            <div className="flex items-end gap-1 h-24">
              {stats.entryFrequency.map(({ date, count }) => {
                const max = Math.max(...stats.entryFrequency.map(e => e.count), 1);
                const heightPct = Math.round((count / max) * 100);
                return (
                  <div
                    key={date}
                    className="flex-1 bg-brand-blue/20 hover:bg-brand-blue/40 rounded-sm transition-colors relative group"
                    style={{ height: `${heightPct}%`, minHeight: '4px' }}
                  >
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      {date}: {count}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Audience distribution */}
        {stats && Object.values(stats.entriesByAudience).some(v => v > 0) && (
          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">
              Audience Distribution
            </h2>
            <div className="space-y-2">
              {Object.entries(stats.entriesByAudience)
                .filter(([, count]) => count > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([audience, count]) => {
                  const pct = Math.round((count / (stats.totalEntries || 1)) * 100);
                  return (
                    <div key={audience} className="flex items-center gap-3">
                      <span className="text-sm text-slate-600 capitalize w-24">{audience}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-orange rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 font-mono w-8 text-right">{count}</span>
                    </div>
                  );
                })}
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Only entries with a primary audience set are shown.
            </p>
          </section>
        )}

        {stats?.totalEntries === 0 && (
          <div className="text-center py-20 bg-white border border-slate-200 rounded-xl">
            <BarChart3 size={40} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No entries yet. Start logging to see patterns.</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}